/**
 * The trial copy on the device (decisions 22, 24; part 29a's table TR, part 35a's TR2′ and ST5; part 36a, principle
 * C2): the relay signs the account's trial clock, the device keeps the newest copy and decides from it alone, offline
 * too, whether the writer may change their books. Pure but the signature check, which takes WebCrypto.
 */
import { fieldsOf, isAccountId } from './wire.ts';
import type { AccountId, SignedCopy } from './wire.ts';
import { fromBase64Url } from './bytes.ts';

/** The copy's signature: ECDSA on P-256 over SHA-256 (the relay's `RELAY_SIGNATURE`). */
export const RELAY_KEY = { name: 'ECDSA', namedCurve: 'P-256' } as const;
export const RELAY_SIGNATURE = { name: 'ECDSA', hash: 'SHA-256' } as const;

/** What a copy says (TR2′, format 2): the account, the trial's end (null: a guest), whether paid, and when issued. */
export interface TrialCopy {
  readonly account: AccountId;
  readonly trialEndsAt: number | null;
  readonly paid: boolean;
  readonly issuedAt: number;
}

/** A copy kept: as sent (to keep and check again) and as read. */
export interface KeptCopy {
  readonly signed: SignedCopy;
  readonly copy: TrialCopy;
}

/** The copy's fields, in the order the relay writes them (TR2: the bytes signed are exactly these). */
const FIELDS = ['version', 'account', 'trialEndsAt', 'paid', 'issuedAt'];

function timeOf(value: unknown): number | null {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 ? value : null;
}

/** TR2′: the copy's bytes read: exactly its five fields in order, format 2; null otherwise. */
export function readCopy(bytes: Uint8Array): TrialCopy | null {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(bytes));
  } catch {
    return null;
  }
  const fields = fieldsOf(value);
  if (fields === null || Object.keys(fields).join() !== FIELDS.join()) return null;
  const account = fields['account'];
  const ends = fields['trialEndsAt'];
  const trialEndsAt = ends === null ? null : timeOf(ends);
  const paid = fields['paid'];
  const issuedAt = timeOf(fields['issuedAt']);
  if (fields['version'] !== 2 || !isAccountId(account) || (ends !== null && trialEndsAt === null) || typeof paid !== 'boolean' || issuedAt === null) return null;
  return { account, trialEndsAt, paid, issuedAt };
}

/** The relay's public key (SPKI) imported for checking copies; null when the build's key isn't a P-256 public key. */
export async function importRelayKey(subtle: SubtleCrypto, spki: Uint8Array<ArrayBuffer>): Promise<CryptoKey | null> {
  try {
    return await subtle.importKey('spki', spki, RELAY_KEY, false, ['verify']);
  } catch {
    return null;
  }
}

/** C2, CL1: the copy when `key` signed its bytes and they read as a copy; null otherwise (never a throw). */
export async function verifyCopy(subtle: SubtleCrypto, key: CryptoKey, signed: SignedCopy): Promise<KeptCopy | null> {
  const bytes = fromBase64Url(signed.copy);
  const signature = fromBase64Url(signed.signature);
  if (bytes === null || signature === null) return null;
  let good: boolean;
  try {
    good = await subtle.verify(RELAY_SIGNATURE, key, signature, bytes);
  } catch {
    return null;
  }
  const copy = good ? readCopy(bytes) : null;
  return copy === null ? null : { signed, copy };
}

/**
 * C2, CL2, CL3: the copy to keep: `fresh` when it names `account` and is newer than `kept` (by `issuedAt`), else
 * `kept`. A copy of another account is never kept.
 */
export function newerCopy(kept: KeptCopy | null, fresh: KeptCopy | null, account: AccountId): KeptCopy | null {
  const stored = kept?.copy.account === account ? kept : null;
  return fresh?.copy.account === account ? newestOf(stored, fresh) : stored;
}

/** CL2, CL3: of `fresh` and `kept`, the one to keep: `kept` only when of `fresh`'s account and at least as new. */
export function newestOf(kept: KeptCopy | null, fresh: KeptCopy): KeptCopy {
  return kept?.copy.account === fresh.copy.account && kept.copy.issuedAt >= fresh.copy.issuedAt ? kept : fresh;
}

/** C2, CL4: the time the copy is judged at: the latest of the device's clock, the copy's issue and the latest seen. */
export function judgedAt(deviceNow: number, latestSeen: number, copy: TrialCopy | null): number {
  return Math.max(deviceNow, latestSeen, copy?.issuedAt ?? 0);
}

/**
 * Deep-fix r2, r5, table TJ′: a copy the relay sent on this page: its issue time, and the device's wall clock and the
 * page's monotonic clock when it came.
 */
export interface Anchor {
  readonly issuedAt: number;
  readonly receivedAt: number;
  readonly receivedMono: number;
}

/**
 * J1–J5: the time judged at from a copy received on this page: its issue time plus the time since, the larger of what
 * the wall clock and the monotonic clock say (a wall clock turned back still has the monotonic one going on; a page
 * asleep, whose monotonic clock may stop, still has the wall clock), never less than nothing.
 */
export function anchoredAt(anchor: Anchor, deviceNow: number, mono: number): number {
  return anchor.issuedAt + Math.max(0, deviceNow - anchor.receivedAt, mono - anchor.receivedMono);
}

/** ST5, C2: whether `copy` lets its account's devices write at `at`: paid, or before the trial's end. */
export function copyWrites(copy: TrialCopy, at: number): boolean {
  return copy.paid || (copy.trialEndsAt !== null && at < copy.trialEndsAt);
}

/** A day, for the trial chip. */
export const DAY_MS = 24 * 60 * 60 * 1000;

/** The trial's whole days left at `at`, rounded up (the chip's "7 days left"); 0 once ended, null when paid or no trial. */
export function daysLeft(copy: TrialCopy, at: number): number | null {
  if (copy.paid || copy.trialEndsAt === null) return null;
  return Math.max(0, Math.ceil((copy.trialEndsAt - at) / DAY_MS));
}
